// src/components/intake/bodies/AmountsBody.jsx
import React from "react";
import { Button, Input, cn } from "../../ui";
import PlaidPanel from "./PlaidPanel";
import { amountValue, formatMoney } from "../intakeRules";
import {
  AMOUNT_LABEL,
  AMOUNT_PLACEHOLDER,
  LINE_ERROR,
  SUGGESTED,
  USE_THIS,
  MANUAL_HEADING,
  MANUAL_LEDE,
  MANUAL_TOTAL,
  PLAID_ERROR,
  PLAID_SUGGEST_NOTE,
} from "../intakeCopy";

// Q5 Debts / Q6 Investments (kind `amounts`): the Fill with Plaid panel above
// the manual line items (a tick and an amount per seeded option, labelled per
// line, "Mortgage, balance owed"). Typing an amount ticks its line. A ticked
// line without an amount shows the line error, tied to its input, when the
// client presses Next (`lineErrors` from the page). No plan gate (decision 22).
// Without `plaid` (the Questions page, which has its own Plaid row) there is
// no panel, and `suggested` ("note" | "error") shows the edit view's note
// over figures already merged into the draft; `Subheading` is "h3" where the
// body sits under an H2.
export default function AmountsBody({
  page,
  draft,
  onChange,
  idPrefix,
  lineErrors = {},
  plaid,
  onPlaidChange,
  suggested = null,
  Subheading = "h2",
}) {
  const options = page.questions[0].options;
  const set = (name, next) =>
    onChange((d) => ({
      ...d,
      lines: { ...d.lines, [name]: { ...d.lines[name], ...next } },
    }));
  const total = Object.values(draft.lines).reduce(
    (sum, l) =>
      l.ticked && amountValue(l.amount) > 0 ? sum + amountValue(l.amount) : sum,
    0,
  );
  return (
    <div className="tw-mt-6 tw-grid tw-gap-6">
      {plaid && (
        <PlaidPanel
          page={page}
          plaid={plaid}
          onChange={onChange}
          onPlaidChange={onPlaidChange}
          idPrefix={idPrefix}
        />
      )}
      {suggested && (
        <p
          role="status"
          className={cn(
            "tw-m-0 tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-6 tw-text-sm",
            suggested === "error" ? "tw-text-danger" : "tw-text-text-muted",
          )}
        >
          {suggested === "error" ? PLAID_ERROR : PLAID_SUGGEST_NOTE}
        </p>
      )}
      <section
        aria-labelledby={`${idPrefix}-manual-h`}
        className="tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-6"
      >
        <Subheading
          id={`${idPrefix}-manual-h`}
          className="tw-m-0 tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-3 tw-text-lg tw-font-bold tw-text-text"
        >
          {MANUAL_HEADING}
          <span className="tw-text-sm tw-font-semibold tw-text-text-muted">
            {MANUAL_TOTAL[page.key]}{" "}
            <b className="tw-text-text">${formatMoney(total)}</b>
          </span>
        </Subheading>
        <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-text-muted">
          {MANUAL_LEDE}
        </p>
        <ul
          role="group"
          aria-labelledby={`${idPrefix}-h1`}
          className="tw-m-0 tw-mt-4 tw-grid tw-list-none tw-grid-cols-1 tw-gap-3 tw-p-0 md:tw-grid-cols-2"
        >
          {options.map((name, i) => {
            const line = draft.lines[name];
            const error = lineErrors[name];
            const offerId = `${idPrefix}-line-${i}-offer`;
            const offered =
              line.offer &&
              amountValue(line.offer) !== amountValue(line.amount);
            const errorId = `${idPrefix}-line-${i}-error`;
            return (
              <li
                key={name}
                className={cn(
                  "tw-grid tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-px-4 tw-py-2",
                  error
                    ? "tw-border-danger"
                    : line.ticked
                      ? "tw-border-text-accent tw-bg-surface-selected"
                      : "tw-border-divider tw-bg-surface",
                )}
              >
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                  <label className="tw-m-0 tw-flex tw-min-h-11 tw-flex-1 tw-items-center tw-gap-3 tw-text-sm tw-font-semibold">
                    <input
                      type="checkbox"
                      className="tw-h-5 tw-w-5 tw-shrink-0 tw-accent-text-accent"
                      checked={line.ticked}
                      onChange={(e) => set(name, { ticked: e.target.checked })}
                    />
                    {name}
                  </label>
                  <div className="tw-relative tw-w-full sm:tw-w-48">
                    <span
                      aria-hidden="true"
                      className="tw-absolute tw-inset-y-0 tw-left-4 tw-flex tw-items-center tw-text-text-muted"
                    >
                      $
                    </span>
                    <Input
                      className="tw-min-h-11 tw-pl-8"
                      inputMode="decimal"
                      placeholder={AMOUNT_PLACEHOLDER[page.key]}
                      aria-label={AMOUNT_LABEL(name, page.key)}
                      aria-describedby={error ? errorId : undefined}
                      invalid={Boolean(error)}
                      value={line.amount}
                      onChange={(e) =>
                        set(name, {
                          amount: e.target.value,
                          ticked: line.ticked || e.target.value !== "",
                        })
                      }
                    />
                  </div>
                </div>
                {offered && (
                  <p className="tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-text-text-muted">
                    <span id={offerId}>{SUGGESTED(line.offer)}</span>
                    <Button
                      variant="text"
                      type="button"
                      aria-describedby={offerId}
                      onClick={() =>
                        set(name, {
                          amount: line.offer,
                          ticked: true,
                          offer: undefined,
                        })
                      }
                    >
                      {USE_THIS}
                    </Button>
                  </p>
                )}
                {error && (
                  <p id={errorId} className="tw-m-0 tw-text-sm tw-text-danger">
                    {LINE_ERROR}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
